import { readonly, ref } from 'vue'

const SHOW_DELAY = 200
const TRICKLE_EVERY = 300
const TRICKLE_CEILING = 90
const FINISH_HOLD = 250
/** Matches the opacity transition in `TopProgress`. */
const FADE = 200

const visible = ref(false)
const progress = ref(0)

let showTimer: ReturnType<typeof setTimeout> | undefined
let trickleTimer: ReturnType<typeof setInterval> | undefined
let hideTimer: ReturnType<typeof setTimeout> | undefined
let running = false

function clearTimers() {
  clearTimeout(showTimer)
  clearInterval(trickleTimer)
  clearTimeout(hideTimer)
}

function start() {
  if (running) return
  running = true
  clearTimers()

  showTimer = setTimeout(() => {
    progress.value = 10
    visible.value = true
    trickleTimer = setInterval(() => {
      progress.value += (TRICKLE_CEILING - progress.value) * 0.1
    }, TRICKLE_EVERY)
  }, SHOW_DELAY)
}

function done() {
  if (!running) return
  running = false
  clearTimers()

  if (!visible.value) {
    progress.value = 0
    return
  }

  progress.value = 100
  hideTimer = setTimeout(() => {
    visible.value = false
    // Reset once the fade is over, or the bar is seen sliding back to the left edge.
    hideTimer = setTimeout(() => {
      progress.value = 0
    }, FADE)
  }, FINISH_HOLD)
}

/**
 * The thin bar at the top of the window that says "something is loading" without covering
 * the page. State is one module-level singleton, drawn by `TopProgress`: **the app renders
 * exactly one `<TopProgress />`**, and whoever knows when the wait starts and ends — the
 * router, for a lazy page chunk — calls `start` and `done`.
 *
 * `start` while running and `done` while idle do nothing, so a redirect's second
 * `beforeEach` and a failed navigation's `afterEach` + `onError` need no bookkeeping.
 */
export function useTopProgress() {
  return {
    visible: readonly(visible),
    progress: readonly(progress),
    start,
    done
  }
}
